import { systemGuideApi } from "@/main/api";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import { KOGTableRow } from "@/main/components/shared/kogBoxGrid/styles";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import * as StylesShared from "@/main/components/shared/styles";
import { TextEllipsis } from "@/main/components/shared/styles";
import { SystemGuideSearchRequest } from "@/main/models/request/systemGuideRequest";
import { SystemGuideSearchGridResponse } from "@/main/models/response/systemGuideResponse";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { getDefaultMessageResultsFound, getTranslateByKey } from "@/main/services/utils";
import { downloadPdfFile } from "@/main/services/utilsFile";
import { TableCell } from "@mui/material";
import { isEmpty, isNil } from "lodash";
import { useEffect, useState } from "react";
import * as S from "./styles";

export interface ISystemGuideGridData {
	isLoading: boolean;
	totalItems: number;
	data: SystemGuideSearchGridResponse[];
}

export const INITIAL_SYSTEM_GUIDE_GRID_STATE: ISystemGuideGridData = {
	isLoading: false,
	totalItems: 0,
	data: [],
};

interface ISystemGuideSearchGridProps {
	gridData: ISystemGuideGridData;
	setGridData: React.Dispatch<React.SetStateAction<ISystemGuideGridData>>;
	searchRequest: SystemGuideSearchRequest | null;
	setSystemGuideSelected: React.Dispatch<React.SetStateAction<SystemGuideSearchGridResponse | undefined>>;
}

const SystemGuideSearchGrid: React.FC<ISystemGuideSearchGridProps> = ({
	gridData,
	setGridData,
	searchRequest,
	setSystemGuideSelected,
}) => {
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [hasShowMore, setHasShowMore] = useState(false);
	const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
	const [loadingDownloadFile, setLoadingDownloadFile] = useState(false);

	useEffect(() => {
		setHasShowMore(gridData.data.length < gridData.totalItems);
	}, [gridData.data, gridData.totalItems]);

	useEffect(() => {
		setPageIndex(PaginationEnum.PageIndexIntial);
	}, [searchRequest]);

	const titleGrid = getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems);

	const rowHeaders = [
		getTranslateByKey("information.fileName"),
		getTranslateByKey("information.insurer"),
		getTranslateByKey("information.whoCanSee"),
		getTranslateByKey("information.language"),
		getTranslateByKey("information.userName"),
		<StylesShared.RowHeaderTextCenter key="5">
			{getTranslateByKey("information.status")}
		</StylesShared.RowHeaderTextCenter>,
	];

	function renderStatus({ active }: SystemGuideSearchGridResponse) {
		const bgColor = active ? "#00cc66" : "#ff0000";
		const statusName = active ? getTranslateByKey("statusRegister.active") : getTranslateByKey("information.inactive");

		return (
			<div
				className="d-flex align-items-center justify-content-center"
				style={{
					backgroundColor: bgColor,
					height: 30,
					width: 100,
					borderRadius: 5,
					paddingLeft: "20px",
					paddingRight: "20px",
					color: "#fff",
					fontWeight: 500,
				}}
			>
				{statusName}
			</div>
		);
	}

	function onLoadInfiniteScroll(request: SystemGuideSearchRequest) {
		setIsLoadingInfiniteScroll(true);

		systemGuideApi
			.getSearchGrid(request)
			.then((response) => {
				let resultData: SystemGuideSearchGridResponse[] = [];
				let resultTotalItems: number = 0;
				if (response.data) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}

				setPageIndex(request.pageIndex ?? PaginationEnum.PageIndexIntial);
				setGridData((prev) => {
					const existingKeys = new Set(prev.data.map((row) => row.guidSystemGuide));
					const newData = resultData.filter((item) => !existingKeys.has(item.guidSystemGuide));

					return {
						isLoading: false,
						totalItems: resultTotalItems,
						data: [...prev.data, ...newData],
					};
				});
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	function onLoadSearchGrid() {
		if (!searchRequest || isLoadingInfiniteScroll) return;

		onLoadInfiniteScroll({
			...searchRequest,
			pageIndex: pageIndex + 1,
		});
	}

	function handleRowBoxGrid(
		item: SystemGuideSearchGridResponse,
		event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
	) {
		event.stopPropagation();
		setSystemGuideSelected(item);
	}

	function downloadSystemGuideFile(
		event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
		{ guidFileImport, fileImportName }: SystemGuideSearchGridResponse,
	) {
		event.stopPropagation();

		if (loadingDownloadFile || isEmpty(guidFileImport) || isNil(guidFileImport)) return;

		setLoadingDownloadFile(true);
		systemGuideApi
			.downloadImportFile(guidFileImport)
			.then((response) => {
				const base64 = response.data.result;
				downloadPdfFile(base64, fileImportName);
			})
			.finally(() => setLoadingDownloadFile(false));
	}

	if (gridData.isLoading) return <LoadingComponent />;
	if (gridData.data.length === 0 && searchRequest === null) return null;
	if (gridData.data.length === 0 && searchRequest !== null)
		return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data.length === 0 && !gridData.isLoading) return null;

	return (
		<>
			<S.Container>
				<BoxGrid
					title={titleGrid}
					rowHeaders={rowHeaders}
					infiniteScrollConfig={{
						dataLength: gridData.data.length,
						fetchData: () => onLoadSearchGrid(),
						hasMore: hasShowMore,
						isLoading: isLoadingInfiniteScroll,
					}}
				>
					{gridData.data.map((item) => (
						<KOGTableRow
							key={item.guidSystemGuide}
							id={`id-searchAnnouncement-tableRow-result-${item.guidSystemGuide}`}
							onClick={(e) => handleRowBoxGrid(item, e)}
						>
							<TableCell>
								<TextEllipsis title={item.fileImportName}>
									<S.LinkText $isDisabled={loadingDownloadFile} onClick={(e) => downloadSystemGuideFile(e, item)}>
										{item.fileImportName}
									</S.LinkText>
								</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={item.insurerName}>{item.insurerName}</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={item.companyTypeName}>{item.companyTypeName}</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={item.cultureName}>{item.cultureName}</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={item.userName}>{item.userName}</TextEllipsis>
							</TableCell>

							<TableCell>
								<S.AreaStatus>{renderStatus(item)}</S.AreaStatus>
							</TableCell>
						</KOGTableRow>
					))}
				</BoxGrid>
			</S.Container>
		</>
	);
};

export default SystemGuideSearchGrid;
